import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatLastBuiltDate } from "@/lib/date";
import {
  agentQueries,
  JobState,
  usePersonasQuery,
  usePersonaWithDocumentsQuery,
} from "@/api/personas";
import { PersonaAvatar } from "@/components/PersonaAvatar";
import { Button } from "@/components/ui/button";
import { useState, useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils.ts";
import { Link } from "react-router-dom";

const STATUS_TO_COLOR_MAP = {
  failed: "text-red-500",
  building: "text-orange-500",
  built: "text-green-500",
};

const getStatusColor = (status: string) =>
  STATUS_TO_COLOR_MAP[status as keyof typeof STATUS_TO_COLOR_MAP] ??
  "text-gray-500";

const STATUS_TO_TEXT_MAP = {
  failed: "Failed",
  building: "Building",
  built: "Built",
};

const getStatusText = (status: string) =>
  STATUS_TO_TEXT_MAP[status as keyof typeof STATUS_TO_TEXT_MAP] ?? "Not Found";

const ITEMS_PER_PAGE = 10;

type SortField = "name";
type SortDirection = "asc" | "desc";

interface SortState {
  field: SortField;
  direction: SortDirection;
}

function SortIcon({
  field,
  sortState,
}: {
  field: SortField;
  sortState: SortState;
}) {
  if (sortState.field !== field) {
    return <ArrowUpDown className="ml-2 h-4 w-4" />;
  }
  return sortState.direction === "asc" ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

function AgentStatusCell({ personaId }: { personaId: string }) {
  const { data, isError } = usePersonaWithDocumentsQuery(personaId);

  if (!data) {
    return <Skeleton className="h-4 w-24" />;
  }

  if (isError) {
    return (
      <span className={getStatusColor("failed")}>
        {getStatusText("failed")}
      </span>
    );
  }

  const status = data.documents.knowledge_graph_status || "failed";
  const lastBuiltDate = data.documents.last_built
    ? formatLastBuiltDate(data.documents.last_built)
    : "Never";

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger>
        <span className={getStatusColor(status)}>{getStatusText(status)}</span>
      </TooltipTrigger>
      <TooltipContent side="right">
        Last Updated: {lastBuiltDate}
      </TooltipContent>
    </Tooltip>
  );
}

const JOB_STATE_COLOR_MAP = {
  success: "bg-green-500",
  queued: "bg-gray-500",
  running: "bg-yellow-500",
  failed: "bg-red-500",
} satisfies Record<JobState, string>;

function AgentRow({ agent }: { agent: { persona_id: string; name: string } }) {
  const { data } = usePersonaWithDocumentsQuery(agent.persona_id);
  const {
    data: jobStatus,
    isPending: jobStatusPending,
    isError: jobStatusError,
  } = useQuery(agentQueries.agentJobStatus(agent.persona_id));

  if (!data) {
    return (
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-24" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-24" />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <PersonaAvatar persona={data.persona} className="h-10 w-10" />
          <span className="font-medium">{data.persona.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <AgentStatusCell personaId={agent.persona_id} />
      </TableCell>
      <TableCell>
        {jobStatusPending ? (
          <Skeleton className="h-4 w-24" />
        ) : jobStatusError ? (
          "Error fetching job status"
        ) : jobStatus.length === 0 ? (
          "No runs"
        ) : (
          <div className="flex flex-row gap-2 items-center">
            {jobStatus.map((job) => (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Link to={job.url} target="_blank">
                    <div
                      className={cn("w-4 h-4", JOB_STATE_COLOR_MAP[job.state])}
                    />
                  </Link>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col items-center">
                  <span>
                    State: <span className="capitalize">{job.state}</span>
                  </span>
                  {job.execution_date ? (
                    <span>
                      Execution date: {formatLastBuiltDate(job.execution_date)}
                    </span>
                  ) : null}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

export function AgentsStatusTable() {
  const [currentPage, setCurrentPage] = useState(1);
  const [nameFilter, setNameFilter] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    field: "name",
    direction: "asc",
  });

  const { data: personas, isLoading: isLoadingPersonas } = usePersonasQuery();

  const filteredAndSortedPersonas = useMemo(() => {
    if (!personas) return [];

    const filtered = personas.filter(
      (agent) =>
        agent.name.toLowerCase().includes(nameFilter.toLowerCase()) ||
        agent.persona_id.toLowerCase().includes(nameFilter.toLowerCase()),
    );

    return filtered.sort((a, b) => {
      const direction = sortState.direction === "asc" ? 1 : -1;
      return direction * a.name.localeCompare(b.name);
    });
  }, [personas, nameFilter, sortState]);

  const handleSort = (field: SortField) => {
    setSortState((prev) => ({
      field,
      direction:
        prev.field === field
          ? prev.direction === "asc"
            ? "desc"
            : "asc"
          : "asc",
    }));
  };

  if (isLoadingPersonas) {
    return <div>Loading agents...</div>;
  }

  const totalPages = Math.ceil(
    (filteredAndSortedPersonas?.length || 0) / ITEMS_PER_PAGE,
  );
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentAgents =
    filteredAndSortedPersonas?.slice(startIndex, endIndex) || [];

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by agent name..."
          value={nameFilter}
          onChange={(e) => {
            setNameFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="pl-8"
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer"
              onClick={() => handleSort("name")}
            >
              <div className="flex items-center">
                Agent
                <SortIcon field="name" sortState={sortState} />
              </div>
            </TableHead>
            <TableHead>Knowledge Base Status</TableHead>
            <TableHead>Last Jobs</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {currentAgents.map((agent) => (
            <AgentRow key={agent.persona_id} agent={agent} />
          ))}
          {currentAgents.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={2}
                className="text-center py-8 text-muted-foreground"
              >
                No agents found matching the search
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {startIndex + 1}-
          {Math.min(endIndex, filteredAndSortedPersonas?.length || 0)} of{" "}
          {filteredAndSortedPersonas?.length || 0} agents
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
